// The browser's stored data belongs to whoever is signed in.
//
// The API cache is keyed by the user (cacheUtils), so one user's entries are
// never read for another. The rest of what's stored for a user isn't keyed
// (reminders, the bug report's logs), so it's cleared whenever the session's
// user changes (store.ts watches `user.sub`): sign-out, a rejected token
// refresh, a 401 that ends the session, or a new sign-in landing on top of an
// old one, including a sign-out that leaves the page (signOutAndLeave): the
// session tells its subscribers, with `leaving` set, before it leaves.

import { cache } from "../utils/cacheUtils";
import { clearDiagnostics } from "@jkast90/kast-common/diagnostics";
import { currentSubject } from "../utils/userScope";
import { session } from "./session";

/** Whose data is in storage now, so a different sign-in can clear it. */
export const DATA_OWNER_KEY = "linktapestry_data_owner";

/** Stored for the signed-in user without a per-user key. */
export const UNSCOPED_USER_DATA_KEYS = [
  "note_reminders",
  "linktapestry_cache_status",
  // Left by the old API history and telemetry, so they go from browsers
  // that still hold them.
  "linktapestry_api_call_history",
  "linktapestry_state_snapshots",
  "linktapestry_telemetry_data",
  "linktapestry_breadcrumbs",
  // The pre-per-user selected workspace; it's now `selectedWorkspace:<user>`.
  "selectedWorkspace",
  DATA_OWNER_KEY,
];

/**
 * Remove every user's cached API data and the signed-in user's unkeyed data,
 * and stop reading or writing the cache until a user is adopted again.
 * Each user's selected workspace is kept under its own key, so it survives
 * signing out and back in.
 */
export function clearUserData(): void {
  cache.clear();
  cache.resetStatus();
  cache.setUser(null);
  clearDiagnostics();
  for (const key of UNSCOPED_USER_DATA_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}

/**
 * Point the cache at the user whose tokens are now stored. If the data in
 * storage belongs to someone else (or to no one recorded), clear it first.
 * Returns true when the cache's user changed or storage was cleared, so the
 * store must drop whatever it holds and reload.
 */
export function adoptSignedInUser(
  subject: string | null = currentSubject(),
): boolean {
  if (!subject) return false;
  const previous = cache.getUser();
  let owner: string | null = null;
  try {
    owner = localStorage.getItem(DATA_OWNER_KEY);
  } catch {
    // ignore
  }
  const foreign = owner !== subject;
  if (foreign) {
    clearUserData();
    try {
      localStorage.setItem(DATA_OWNER_KEY, subject);
    } catch {
      // ignore
    }
  }
  cache.setUser(subject);
  return foreign || previous !== subject;
}

/**
 * Sign out (revoking the refresh token) and leave the page with `leave` (the
 * login site's logout, or signing in again). store.ts's subscriber clears
 * this user's data first; the page goes without rendering signed out.
 */
export function signOutAndLeave(leave: () => void): Promise<void> {
  return session.signOut({ leave });
}
